import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const contactFormUrl = 'https://readdy.ai/api/form/d9ng7i68mbnljin5a06g';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormSubmitted(true);
      setFormSubmitting(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, String(value));
        }
      });

      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_e) { /* ignore parse error */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试。');
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <>
      <header>
        <Navbar />
      </header>

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 py-3">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="bg-background-900 py-16 md:py-20 text-center">
          <div className="w-full px-4 md:px-6">
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-primary-500 rounded-2xl">
              <span className="text-background-50 font-bold text-2xl font-heading">97</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-background-50 font-heading mb-4">关于97影视</h1>
            <p className="text-background-300 max-w-2xl mx-auto text-sm leading-relaxed">
              97影视成立于2020年，是一家专注于提供高清免费在线观影服务的影视平台。我们始终坚持以用户体验为核心，致力于为全球华语影迷打造极致的在线观影体验。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">品牌故事</h2>
            <div className="prose max-w-none">
              <p className="text-foreground-700 leading-relaxed mb-4 text-sm">
                97影视的诞生源于创始团队对影视的热爱。在影视行业深耕多年后，我们发现很多优质内容因为付费门槛而无法触达更多观众。于是，97影视应运而生——一个完全免费、无广告打扰的高清影视平台。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4 text-sm">
                从最初的几十部影片起步，到如今拥有超过50万部影视资源的庞大内容库，97影视始终坚守初心：让每个人都能轻松享受高清观影的乐趣。我们投入大量资源优化播放技术，确保即使是在网络条件不佳的情况下，用户依然能够流畅观看。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4 text-sm">
                97影视的名字灵感来源于1997年——那是中国互联网萌芽的年代，也是无数经典影视作品诞生的黄金时期。我们以此命名，既是对经典的致敬，也代表着我们对优质内容的执着追求。
              </p>
              <p className="text-foreground-700 leading-relaxed text-sm">
                未来，97影视将继续拓展内容库，优化观影体验，为更多影迷带来优质的免费观影服务。我们相信，好的内容应该被更多人看到，而97影视就是连接优质内容与观众的桥梁。
              </p>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-8 text-center">核心价值</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-shield-check-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="font-bold text-foreground-900 mb-2 font-heading">完全免费</h3>
                <p className="text-sm text-foreground-600">无需注册，无需付费，所有内容完全免费开放。</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                  <i className="ri-eye-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="font-bold text-foreground-900 mb-2 font-heading">纯净体验</h3>
                <p className="text-sm text-foreground-600">零广告干扰，专注观影，还原最纯粹的视听享受。</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-speed-up-line text-2xl text-secondary-500"></i>
                </div>
                <h3 className="font-bold text-foreground-900 mb-2 font-heading">技术驱动</h3>
                <p className="text-sm text-foreground-600">持续优化播放技术，确保高清秒播不卡顿。</p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: '张先生', text: '用了97影视快两年了，画质清晰播放流畅，最重要是真的没有广告！强烈推荐给所有喜欢看电影的朋友。', rating: 5 },
                { name: '李女士', text: '作为一个追剧达人，97影视就是我每天的快乐源泉。更新速度快，新剧上线就能看，太方便了。', rating: 5 },
                { name: '小王同学', text: '学生党福利！免费高清不用花钱充会员，课余时间看个电影放松一下，体验比付费平台还好。', rating: 5 },
                { name: '陈老师', text: '资源丰富，分类清晰，想找什么片子都能找到。画质和流畅度都让人满意，已经推荐给身边所有朋友了。', rating: 5 },
                { name: '刘工程师', text: '从技术角度来说，97影视的播放优化做得确实不错。即使在网络不太好的情况下也能流畅播放。', rating: 4 },
                { name: '赵同学', text: '界面简洁易用，没有乱七八糟的弹窗和广告。观影体验很纯粹，这就是我一直用97影视的原因。', rating: 5 },
              ].map((review, index) => (
                <div key={index} className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'}`}></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">&ldquo;{review.text}&rdquo;</p>
                  <p className="text-xs text-foreground-500 font-medium">{review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-12 md:py-16 bg-background-100" id="contact">
          <div className="w-full px-4 md:px-6 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-2 text-center">联系我们</h2>
            <p className="text-foreground-500 text-sm text-center mb-8">如有任何问题或建议，欢迎通过以下表单与我们联系</p>

            {formSubmitted ? (
              <div className="bg-background-50 rounded-lg p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-check-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-900 font-heading mb-2">提交成功</h3>
                <p className="text-foreground-600 text-sm">感谢您的留言，我们会尽快与您联系。</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-background-50 rounded-lg p-6" data-readdy-form>
                {formError && (
                  <div className="mb-4 p-3 bg-primary-50 text-primary-700 text-sm rounded-lg">
                    {formError}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">留言</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  />
                </div>
                <div className="antispam-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formSubmitting ? '提交中...' : '提交留言'}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于97影视",
            "description": "97影视成立于2020年，是一家专注于提供高清免费在线观影服务的影视平台。",
            "url": typeof window !== 'undefined' ? window.location.href : '',
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": typeof window !== 'undefined' ? window.location.origin + '/' : '/' },
              { "@type": "ListItem", "position": 2, "name": "关于我们" },
            ]
          })
        }}
      />
    </>
  );
}